<template>
  <el-card
    shadow="never"
    class="cursor-pointer hover:shadow-lg transition-all duration-200 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
    tabindex="0"
    role="button"
    @click="$emit('click', deployment)"
    @keyup.enter="$emit('click', deployment)">
    <div class="space-y-3">
      <!-- Deployment Name & Header -->
      <div class="flex items-start justify-between gap-2">
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <div
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <Server class="h-4 w-4 text-gray-900 dark:text-white" />
          </div>
          <div class="min-w-0 flex-1">
            <p
              class="truncate text-base font-bold text-gray-900 dark:text-white">
              {{
                deployment.deployment_name ||
                i18n.t('browserExtension.unknownDeployment')
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Stats Row -->
      <div
        class="flex items-center gap-4 rounded-lg bg-gray-50 px-3 py-2.5 dark:bg-gray-900/50">
        <!-- Sessions -->
        <div class="flex flex-1 items-baseline gap-1.5">
          <span
            class="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
            {{ deployment.count }}
          </span>
          <span class="text-xs text-gray-500 dark:text-gray-400">
            {{ i18n.t('browserExtension.sessions') }}
          </span>
        </div>

        <!-- Divider -->
        <div class="h-8 w-px bg-gray-200 dark:bg-gray-700"></div>

        <!-- Active / Total Users -->
        <div class="flex flex-1 items-baseline justify-end gap-1.5">
          <span
            class="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
            {{ deployment.active_users || 0 }} /
            {{ deployment.total_deployment_users || 0 }}
          </span>
          <span class="text-xs text-gray-500 dark:text-gray-400">
            {{ i18n.t('users') }}
          </span>
        </div>
      </div>

      <!-- User Penetration Progress Bar -->
      <div v-if="hasUserData" class="space-y-1.5">
        <div class="flex items-center justify-between text-xs">
          <span class="text-gray-500 dark:text-gray-400">
            {{ i18n.t('browserExtension.userPenetration') }}
          </span>
          <span
            class="font-semibold tabular-nums text-gray-900 dark:text-white">
            {{ penetrationPercentage }}%
          </span>
        </div>
        <div
          class="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
          role="progressbar"
          :aria-valuenow="penetrationPercentage"
          aria-valuemin="0"
          aria-valuemax="100">
          <div
            class="h-full rounded-full bg-primary transition-all duration-500 ease-out"
            :style="{
              width: `${penetrationPercentage}%`,
              'min-width': '4px',
            }"></div>
        </div>
      </div>

      <!-- Last Usage Footer -->
      <div
        v-if="deployment.latestUsage"
        class="rounded-md flex items-center justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-700/50">
        <div class="flex items-center gap-2">
          <Clock class="h-3.5 w-3.5 text-gray-400" />
          <span class="text-xs text-gray-500 dark:text-gray-400">
            {{ i18n.t('browserExtension.lastUsage') }}
          </span>
        </div>
        <span
          class="text-xs font-medium tabular-nums text-gray-900 dark:text-white">
          {{ displayDate(dayjs(deployment.latestUsage).toDate()) }}
        </span>
      </div>
    </div>
  </el-card>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Server, Clock } from 'lucide-vue-next'
  import dayjs from 'dayjs'
  import { displayDate } from '@/utils/formatter/date'
  import type { BrowserLastUsageBySoftware } from '@/stores/browserextension/browserextension.store'

  const props = defineProps<{
    deployment: BrowserLastUsageBySoftware
  }>()

  const emit = defineEmits<{
    (e: 'click', deployment: BrowserLastUsageBySoftware): void
  }>()

  const i18n = useI18n()

  const hasUserData = computed(() => {
    return (
      props.deployment.total_deployment_users &&
      Number(props.deployment.total_deployment_users) > 0
    )
  })

  const penetrationPercentage = computed(() => {
    if (!hasUserData.value) return 0
    return Math.round(
      (Number(props.deployment.active_users || 0) /
        Number(props.deployment.total_deployment_users)) *
        100
    )
  })
</script>
